import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import apiClient, { setAuthToken, setRefreshToken } from "../services/api.js";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem("auth_token"));
  const [refreshToken, setRefresh] = useState(() => localStorage.getItem("refresh_token"));
  const [member, setMember] = useState(() => {
    const stored = localStorage.getItem("member");
    if (!stored) return null;
    try {
      return JSON.parse(stored);
    } catch {
      localStorage.removeItem("member");
      return null;
    }
  });
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    setAuthToken(accessToken);
    setRefreshToken(refreshToken);
  }, [accessToken, refreshToken]);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setRefresh(null);
    setMember(null);
    localStorage.removeItem("member");
    setAuthToken(null);
    setRefreshToken(null);
  }, []);

  const login = useCallback((data) => {
    setAccessToken(data.access_token);
    setRefresh(data.refresh_token);
    setMember(data.member);
    localStorage.setItem("member", JSON.stringify(data.member));
    setAuthReady(true);
  }, []);

  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  const updateMember = useCallback((nextMember) => {
    setMember(nextMember);
    localStorage.setItem("member", JSON.stringify(nextMember));
  }, []);

  const fetchMe = useCallback(async () => {
    if (!accessToken) return;
    try {
      const { data } = await apiClient.get("/auth/me");
      updateMember(data);
      return data;
    } catch (error) {
      if (error?.response?.status === 401 || error?.response?.status === 422) {
        clearSession();
      }
      throw error;
    }
  }, [accessToken, clearSession, updateMember]);

  useEffect(() => {
    let active = true;
    const bootstrapSession = async () => {
      if (!accessToken) {
        if (active) setAuthReady(true);
        return;
      }
      try {
        await fetchMe();
      } catch (error) {
        if (active && !(error?.response?.status === 401 || error?.response?.status === 422)) {
          setAuthReady(true);
        }
        return;
      }
      if (active) setAuthReady(true);
    };
    bootstrapSession();
    return () => {
      active = false;
    };
  }, [accessToken, fetchMe]);

  const value = useMemo(
    () => ({
      accessToken,
      refreshToken,
      member,
      authReady,
      login,
      logout,
      updateMember,
      fetchMe
    }),
    [accessToken, refreshToken, member, authReady, login, logout, updateMember, fetchMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
